import type { Lab } from "@/lib/api_client";

/**
 * Resolves the lab that applies to a specific lab visit.
 *
 * Rule: `lab_visits.labId` is authoritative once set. Falls back to the
 * profile's `labId` when the visit doesn't have its own.
 *
 *   - B2B visits: `visit.labId` is populated from `company.labId` at
 *     creation (`accounts/routes.ts`) and is never overwritten by the
 *     profile (guarded in `lifecycle.ts`).
 *   - Non-B2B visits: `visit.labId` starts null. `sendRequisition`
 *     resolves via this same rule (profile.labId fallback) at POST time.
 *     The `pendingRequisition → requisitioned` transition then snapshots
 *     `profile.labId` onto `visit.labId` so future reads are stable
 *     even if the user later changes their preferred lab.
 *
 * This helper centralizes the rule so every caller (page step router,
 * Nylig-rekvirert "Brevpost" badge, snail-mail row filter) makes the
 * same decision. Mixing visit.labId vs profile.labId across call sites
 * produced visible bugs (e.g. snail-mail step rendering for B2B visits
 * whose company lab differed from the user's profile preference).
 */
export function resolveVisitLab(
  visit: { labId?: string | null },
  profileLabId: string | null | undefined,
  labsById: Map<string, Lab>,
): Lab | undefined {
  const id = visit.labId ?? profileLabId ?? null;
  if (!id) return undefined;
  return labsById.get(id);
}

/** Convenience: build a `Map<labId, Lab>` once and pass it to the resolver. */
export function buildLabsById(labs: Lab[] | undefined): Map<string, Lab> {
  const map = new Map<string, Lab>();
  for (const l of labs ?? []) map.set(l.id, l);
  return map;
}
